import {
    forwardRef,
    ReactElement,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react'

import Quill from 'quill'

import { RichTextValue } from './types'

interface RichTextEditorProps {
    readOnly?: boolean
    onTextChange?: (...args: any[]) => void
    onSelectionChange?: (...args: any[]) => void
    defaultValue?: RichTextValue
}

const RichTextEditor = forwardRef<Quill | undefined, RichTextEditorProps>(
    (
        { readOnly, defaultValue, onTextChange, onSelectionChange },
        ref
    ): ReactElement => {
        const quillRef = useRef<Quill | undefined>(undefined)

        const containerRef = useRef<HTMLDivElement>(null)
        const defaultValueRef = useRef(defaultValue)
        const onTextChangeRef = useRef(onTextChange)
        const onSelectionChangeRef = useRef(onSelectionChange)

        useLayoutEffect(() => {
            onTextChangeRef.current = onTextChange
            onSelectionChangeRef.current = onSelectionChange
        })

        useEffect(() => {
            quillRef.current?.enable(!readOnly)
        }, [ref, readOnly])

        useEffect(() => {
            const container = containerRef.current
            const editorContainer = container?.appendChild(
                container.ownerDocument.createElement('div')
            )
            const quill = new Quill(editorContainer!, {
                theme: readOnly ? undefined : 'snow',
                readOnly,
            })

            quillRef.current = quill
            if (typeof ref === 'function') {
                ref(quill)
            } else if (ref) {
                ref.current = quill
            }

            if (defaultValueRef.current) {
                quill.setContents(defaultValueRef.current)
            }

            quill.on(Quill.events.TEXT_CHANGE, (...args) => {
                onTextChangeRef.current?.(...args)
            })

            quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
                onSelectionChangeRef.current?.(...args)
            })

            return () => {
                if (quillRef.current) {
                    quillRef.current = undefined
                    if (typeof ref === 'function') {
                        ref(undefined)
                    } else if (ref) {
                        ref.current = undefined
                    }
                }
                if (container) {
                    container.innerHTML = ''
                }
            }
        }, [readOnly, ref])

        return <div ref={containerRef}></div>
    }
)

RichTextEditor.displayName = 'RichTextEditor'

export default RichTextEditor
